import dbStorageService from '../dbStorageService';
import cacheHelper from './cacheHelper';

/**
 * Helper module for member management in circles
 */
const memberHelper = {
    /**
     * Gets member details by user ID
     * @param {number|string} userId - The user ID to look up
     * @returns {Promise<Object|null>} Member details including circles they belong to
     */
    async getMemberByUserId(userId) {
        try {
            if (!userId) return null;

            // Ensure userId is an integer
            const parsedUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
            if (isNaN(parsedUserId)) return null;

            // Check cache first
            const cached = cacheHelper.getCachedUser(parsedUserId);
            if (cached) return cached;

            const circles = await dbStorageService.getAllRecords('Circle');
            let memberDetails = null;

            for (const circle of circles) {
                if (!circle.members) continue;

                let members;
                try {
                    members = JSON.parse(circle.members);
                } catch (e) {
                    console.error('Failed to parse members for circle:', circle.uuid, e);
                    continue;
                }

                const member = members.find(m => {
                    const memberUserId = typeof m.userId === 'string' ? parseInt(m.userId, 10) : m.userId;
                    return memberUserId === parsedUserId;
                });

                if (member) {
                    if (!memberDetails) {
                        memberDetails = {
                            ...member,
                            circles: []
                        };
                    }
                    memberDetails.circles.push({
                        uuid: circle.uuid,
                        name: circle.name,
                        role: member.role
                    });
                }
            }

            // Cache the result if found
            if (memberDetails) {
                cacheHelper.setCachedUser(parsedUserId, memberDetails);
            }

            return memberDetails;
        } catch (error) {
            // Gracefully handle missing Circle table
            if (error.name === 'NotFoundError' || error.message.includes('Circle')) {
                console.warn('[memberHelper] Circle table not found, returning null member');
                return null;
            }
            console.error(`Error getting member for userId ${userId}:`, error);
            throw new Error(`Failed to get member details: ${error.message}`);
        }
    },

    /**
     * Get contact details for a user
     * @param {number|string} contactUserId - The user ID to look up
     * @returns {Promise<Object|null>} Basic contact information
     */
    async getMemberDetailsForContact(contactUserId) {
        try {
            const member = await this.getMemberByUserId(contactUserId);
            if (!member) {
                return null;
            }

            return {
                firstName: member.firstName,
                lastName: member.lastName,
                email: member.email,
                displayName: `${member.firstName} ${member.lastName}`
            };
        } catch (error) {
            console.error(`Error getting member details for contact ${contactUserId}:`, error);
            throw new Error(`Failed to get contact member details: ${error.message}`);
        }
    },

    /**
     * Gets all members across all circles
     * @returns {Promise<Array>} Array of all members with their circles
     */
    async getAllMembers() {
        try {
            const circles = await dbStorageService.getAllRecords('Circle');
            const membersMap = new Map();

            circles.forEach(circle => {
                if (!circle.members) return;

                let members;
                try {
                    members = JSON.parse(circle.members);
                } catch (e) {
                    console.error('Failed to parse members for circle:', circle.uuid, e);
                    return;
                }

                members.forEach(member => {
                    if (!member.userId) return;

                    membersMap.set(member.userId, {
                        ...member,
                        circles: membersMap.has(member.userId)
                            ? [...membersMap.get(member.userId).circles, {
                                uuid: circle.uuid,
                                name: circle.name,
                                role: member.role
                            }]
                            : [{
                                uuid: circle.uuid,
                                name: circle.name,
                                role: member.role
                            }]
                    });
                });
            });

            return Array.from(membersMap.values());
        } catch (error) {
            // Gracefully handle missing Circle table
            if (error.name === 'NotFoundError' || error.message.includes('Circle')) {
                console.warn('[memberHelper] Circle table not found, returning empty members list');
                return [];
            }
            console.error('Error getting all members:', error);
            throw new Error('Failed to get all members: ' + error.message);
        }
    },

    /**
     * Gets the circles a member belongs to
     * @param {number|string} userId - The user ID to look up
     * @returns {Promise<Array>} Circles the user belongs to
     */
    async getCirclesByMemberId(userId) {
        try {
            const circles = await dbStorageService.getAllRecords('Circle');
            return circles.filter(circle => {
                const members = JSON.parse(circle.members);
                return members.some(member => member.userId === userId);
            });
        } catch (error) {
            // Gracefully handle missing Circle table
            if (error.name === 'NotFoundError' || error.message.includes('Circle')) {
                return [];
            }
            console.error(`Error getting circles for member ${userId}:`, error);
            throw new Error(`Failed to get member circles: ${error.message}`);
        }
    },

    /**
     * Gets members from circles that contain a specific user
     * @param {number|string} userId - The user ID to find shared circle members for
     * @returns {Promise<Array>} Members that share circles with the specified user
     */
    async getMembersInSameCircles(userId) {
        // circles disabled in this release
        return [];
        try {
            // Check if we're in a logout/cleanup process
            if (!dbStorageService.isInitialized || !dbStorageService.db) {
                console.log('[CircleService] Database not initialized or in cleanup, returning empty members list');
                return [];
            }

            // First check if Circle table exists
            const circleTableExists = await dbStorageService.tableExists('Circle');

            if (!circleTableExists) {
                try {
                    // Database exists but table doesn't - try to create it
                    await dbStorageService.createEmergencyTable('Circle');
                } catch (error) {
                    console.warn('[CircleService] Could not create emergency Circle table:', error);
                    // Return empty array instead of failing
                    return [];
                }
            }

            // Safely try to get all circles
            let circles;
            try {

                circles = await dbStorageService.getAllRecords('Circle');

                // If no circles found, return empty array
                if (!circles || !Array.isArray(circles)) {
                    return [];
                }
            } catch (dbError) {
                // If we get an error accessing the database, it might be during logout
                if (dbError.name === 'InvalidStateError' ||
                    !dbStorageService.isInitialized ||
                    !dbStorageService.db) {
                    console.log('[CircleService] Database connection lost, returning empty members list');
                    return [];
                }
                throw dbError;
            }

            // Filter active circles
            const activeCircles = circles.filter(circle => !circle.isDeleted);

            // Find circles containing the user
            const userCircles = activeCircles.filter(circle => {
                try {
                    const members = JSON.parse(circle.members || '[]');

                    const hasUser = members.some(member => {
                        const isMatch = member.userId === userId && !member.isDeleted;
                        return isMatch;
                    });
                    return hasUser;
                } catch (error) {
                    console.error(`[CircleService] Error parsing members for circle ${circle.uuid}:`, error);
                    return false;
                }
            });

            // Get all unique members from these circles
            const membersMap = new Map();
            userCircles.forEach(circle => {
                try {
                    const members = JSON.parse(circle.members || '[]');
                    members.forEach(member => {
                        if (member.userId !== userId && !member.isDeleted) {
                            // If member already exists, take the highest role
                            if (membersMap.has(member.userId)) {
                                const existingMember = membersMap.get(member.userId);
                                if (member.role > existingMember.role) {
                                    membersMap.set(member.userId, member);
                                }
                            } else {
                                membersMap.set(member.userId, member);
                            }
                        }
                    });
                } catch (error) {
                    console.error(`[CircleService] Error processing members for circle ${circle.uuid}:`, error);
                }
            });

            const members = Array.from(membersMap.values());
            return members;
        } catch (error) {
            console.error('[CircleService] Failed to get circle members:', error);
            // Return empty array instead of throwing an error to avoid breaking notification service
            return [];
        }
    },

    /**
     * Gets a list of users that the current user can assign records to
     * @param {number|string} userId - The ID of the current user
     * @returns {Promise<Array>} Array of { value: userId, label: 'First Last' } objects
     */
    async getAssignableUsersFor(userId) {
        if (!userId) return [];

        try {
            // Get the current user's details first
            const currentUser = await this.getMemberByUserId(userId);
            if (!currentUser) {
                console.warn(`[CircleService] User ${userId} not found in any circles`);
                return []; // No user found
            }

            // Create the entry for the current user
            const userEntries = [{
                value: Number(userId) || 0,
                label: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'Self',
                isSelf: true
            }];

            // Get other circle members
            const otherMembers = await this.getMembersInSameCircles(userId);

            // Add entries for other members
            otherMembers.forEach(member => {
                if (member && member.userId !== undefined && member.userId !== null && (member.firstName || member.lastName)) {
                    userEntries.push({
                        value: Number(member.userId) || 0,
                        label: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
                        isSelf: false
                    });
                }
            });

            // Sort by name - current user always first, then alphabetically by label
            userEntries.sort((a, b) => {
                if (a.isSelf) return -1;
                if (b.isSelf) return 1;
                return a.label.localeCompare(b.label);
            });

            return userEntries;
        } catch (error) {
            console.error(`[CircleService] Error getting assignable users for ${userId}:`, error);
            return []; // Return empty array on error
        }
    }
};

export default memberHelper; 